/**
 * Own-auth endpoint wrappers.
 *
 * Thin promise-returning helpers for the public /auth/* surface
 * (auth-register.json, auth-login.json, auth-verify-email.json,
 * auth-password-reset.json).  They stash the JWT into the auth store on
 * success so callers don't have to thread the token around.
 */
import { apiFetch } from '@/lib/api';
import { APP_EDITION } from '@/lib/config';

import { setAuthToken } from './store';
import type { AuthTokenResponse } from './types';

interface RegisterInput {
  email: string;
  password: string;
  full_name: string;
  company_name?: string;
  /**
   * ISO 3166-1 alpha-2. Drives the org's default jurisdiction and billing
   * currency server-side (`editions.org_defaults`). Omit it rather than
   * guessing: absent resolves to "no local-law depth", a guess resolves to
   * the wrong country's law.
   */
  country?: string;
}

interface LoginInput {
  email: string;
  password: string;
}

export async function registerUser(input: RegisterInput): Promise<AuthTokenResponse> {
  const res = await apiFetch<AuthTokenResponse>('/auth/register', {
    method: 'POST',
    // edition: see APP_EDITION. A recognised Origin outranks it, so this only
    // takes effect where the backend cannot read one — which includes every
    // localhost dev session, where the fallback is the India edition.
    body: { ...input, edition: APP_EDITION },
    skipAuth: true,
  });
  setAuthToken(res.access_token);
  return res;
}

export async function loginUser(input: LoginInput): Promise<AuthTokenResponse> {
  const res = await apiFetch<AuthTokenResponse>('/auth/login', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });
  setAuthToken(res.access_token);
  return res;
}

/**
 * Step 1 of passwordless signup. Provisions a user+org+trial on first
 * contact, then emails a 6-digit code. If the email already has an
 * account it just behaves like ``requestLoginCode``. Step 2 is the same
 * ``verifyLoginCode`` call as login.
 */
export async function requestSignupCode(
  email: string,
  opts: {
    fullName?: string;
    companyName?: string;
    profession?: string;
    phone?: string;
    jobTitle?: string;
    /** ISO 3166-1 alpha-2 — see `RegisterInput.country`. */
    country?: string;
  } = {},
): Promise<void> {
  await apiFetch('/auth/signup/request-code', {
    method: 'POST',
    body: {
      email,
      full_name: opts.fullName,
      company_name: opts.companyName,
      profession: opts.profession,
      phone: opts.phone,
      job_title: opts.jobTitle,
      country: opts.country,
      // Without this the org is provisioned on the India edition in dev, and
      // `org_defaults` pins its jurisdiction to IN whatever country says.
      edition: APP_EDITION,
    },
    skipAuth: true,
  });
}

/**
 * Step 1 of passwordless login.
 *
 * Rejects with NotFoundError (HTTP 404, "No account found with this email")
 * when no active account exists. That is a deliberate backend UX choice that
 * replaced the anti-enumeration always-200 — it lets the form point the
 * visitor at /sign-up instead of advancing them to an OTP step where every
 * guess would fail. It does mean the endpoint confirms which addresses have
 * accounts; the auth rate-limit middleware is what bounds bulk probing.
 * Callers must handle NotFoundError distinctly — both sign-in pages do.
 */
export async function requestLoginCode(email: string): Promise<void> {
  await apiFetch('/auth/login/request-code', {
    method: 'POST',
    body: { email },
    skipAuth: true,
  });
}

/**
 * Step 2 of passwordless login. On success the JWT is stashed in the auth
 * store; callers can redirect immediately.
 */
export async function verifyLoginCode(email: string, code: string): Promise<AuthTokenResponse> {
  const res = await apiFetch<AuthTokenResponse>('/auth/login/verify-code', {
    method: 'POST',
    body: { email, code },
    skipAuth: true,
  });
  setAuthToken(res.access_token);
  return res;
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiFetch('/auth/forgot-password', {
    method: 'POST',
    body: { email },
    skipAuth: true,
  });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiFetch('/auth/reset-password', {
    method: 'POST',
    body: { token, new_password: newPassword },
    skipAuth: true,
  });
}

export async function verifyEmail(token: string): Promise<void> {
  await apiFetch('/auth/verify-email', {
    method: 'POST',
    body: { token },
    skipAuth: true,
  });
}

export async function resendVerificationEmail(email: string): Promise<void> {
  await apiFetch('/auth/resend-verification', {
    method: 'POST',
    body: { email },
    skipAuth: true,
  });
}

/**
 * Best-effort server-side logout — emits the audit-log entry. Callers
 * should clear the local token regardless of whether this call succeeds,
 * so a 401/timeout never traps the user signed-in client-side.
 */
export async function logoutUser(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST' });
  } catch {
    // Swallow — token cleanup happens in the caller either way.
  }
}

/**
 * URL the frontend should redirect to for Google sign-in.  The backend
 * 302s to Google's consent screen; do NOT call via fetch (CORS will fail
 * and the cookie set in /auth/google requires a top-level redirect).
 */
export function googleSignInUrl(): string {
  const base = process.env['NEXT_PUBLIC_API_BASE_URL'];
  if (!base) {
    throw new Error('NEXT_PUBLIC_API_BASE_URL is not set; cannot start Google sign-in.');
  }
  return `${base}/auth/google`;
}

/**
 * GET /auth/wealthorbit-sso?token=… — validates a WealthOrbit-signed JWT
 * and returns where to redirect the user to complete own-auth signup.
 *
 * Public; no LexAI JWT required. Used by the WealthOrbit cross-app SSO
 * landing page that opens lexai.in/auth/wealthorbit?token=…
 */
export interface WealthOrbitSsoResponse {
  email: string;
  name: string | null;
  wo_user_id: string | null;
  redirect_url: string;
  is_existing_user: boolean;
}

export async function exchangeWealthOrbitSso(token: string): Promise<WealthOrbitSsoResponse> {
  const params = new URLSearchParams({ token });
  return apiFetch<WealthOrbitSsoResponse>(
    `/auth/wealthorbit-sso?${params.toString()}`,
    { method: 'GET', skipAuth: true },
  );
}
